
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthProviders } from "./AuthProviders";
import { EmailSignIn } from "./EmailSignIn";
import { GuestContinue } from "./GuestContinue";
import { PasskeySignInButton } from "./PasskeySignInButton";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { Goal } from "@/types/todo";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";
import { isInstalledApp } from "@/lib/platform";


interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuthModal({ open, onOpenChange }: AuthModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const localStorage = useLocalStorage("goals", []);
  const { toast } = useToast();
  const [authError, setAuthError] = useState<string | null>(null);
  const installedApp = isInstalledApp();


  // Track whether the modal was actively opened for login by the user.
  // Only navigate after sign-in when the user intentionally opened the modal.
  const modalOpenedForLogin = useRef(false);

  useEffect(() => {
    if (open) {
      modalOpenedForLogin.current = true;
      sessionStorage.setItem('redirectAfterAuth', window.location.pathname + window.location.search);
      logger.debug("🔄 Saved redirect path:", window.location.pathname + window.location.search);
    }
  }, [open]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug("🔐 Auth event in modal:", event);

      // Only process navigation when the modal was actively used for login
      if (event === 'SIGNED_IN' && modalOpenedForLogin.current) {
        modalOpenedForLogin.current = false; // Reset so subsequent background events are ignored

        try {
          logger.debug("✅ User signed in via modal");
          await syncLocalDataToCloud(session?.user.id);
          onOpenChange(false);

          // Handle pending connection request
          const pendingConnectionUserId = sessionStorage.getItem('pendingConnectionUserId');
          if (pendingConnectionUserId) {
            sessionStorage.removeItem('pendingConnectionUserId');
            navigate(`/connect/${pendingConnectionUserId}`);
            return;
          }

          // Redirect to saved path (never to /auth)
          const redirectPath = sessionStorage.getItem('redirectAfterAuth');
          if (redirectPath && !redirectPath.startsWith('/auth') && redirectPath !== '/') {
            navigate(redirectPath);
            sessionStorage.removeItem('redirectAfterAuth');
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          logger.error("❌ Error after sign in:", error);
        }
      } else if (event === 'PASSWORD_RECOVERY') {
        toast({
          title: "Password Recovery",
          description: "Please check your email for password recovery instructions.",
        });
      }
      // All other events (TOKEN_REFRESHED, SIGNED_OUT, etc.) are handled
      // by the centralized AuthProvider — no navigation here.
    });

    // Handle URL authentication parameters (OAuth / magic link callback)
    const handleHashParams = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken) {
        // This is a real auth callback — mark as active login
        modalOpenedForLogin.current = true;
        logger.debug("🔑 Processing authentication from URL parameters");
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });
          if (error) throw error;
        } catch (error) {
          logger.error("❌ Error processing auth from URL:", error);
          setAuthError("Failed to complete authentication. Please try again.");
        }
      }

      // PKCE recovery: a `?code=` that never turns into a session means the
      // redirect finished in a different storage jar (classic installed-app
      // case). Tell the user instead of failing silently.
      const query = new URLSearchParams(window.location.search);
      if (query.get('code')) {
        modalOpenedForLogin.current = true;
        setTimeout(async () => {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            setAuthError(
              "We couldn't finish that sign-in here. Use Face ID / passkey, or enter the 6-digit code we email you.",
            );
          }
        }, 2500);
      }
    };


    handleHashParams();
    return () => subscription.unsubscribe();
  }, [onOpenChange, navigate, toast]);

  const syncLocalDataToCloud = async (userId: string) => {
    try {
      const localGoals = localStorage.getItem("goals") as Goal[] || [];
      if (localGoals.length > 0) {
        toast({ title: "Syncing data", description: "Moving your local goals to your account..." });
        for (const goal of localGoals) {
          const { data: categoryData } = await supabase
            .from('categories').select('id').eq('name', goal.category).single();
          const { error: goalError } = await supabase.from('goals').insert([{
            title: goal.title, progress: goal.progress, due_date: goal.dueDate,
            category_id: categoryData?.id, user_id: userId
          }]);
          if (goalError) throw goalError;
        }
        localStorage.setItem("goals", []);
        toast({ title: "Data synced", description: "Your goals have been successfully moved to your account." });
      }
    } catch (error: any) {
      logger.error('❌ Error syncing data:', error);
      toast({ title: "Error syncing data", description: "There was an error moving your local goals. Please try again.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Welcome to Goal Digger</DialogTitle>
          <DialogDescription className="sr-only">
            Sign in with Google, sign in with email, or continue as a guest.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6">
          {authError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <p>{authError}</p>
            </div>
          )}
          <div className="space-y-4">
            {installedApp ? (
              <>
                <PasskeySignInButton />
                <AuthProviders />
              </>
            ) : (
              <>
                <AuthProviders />
                <PasskeySignInButton />
              </>
            )}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>
          </div>
          <EmailSignIn />
          <GuestContinue onClose={() => onOpenChange(false)} />

          <p className="text-center text-sm text-muted-foreground">
            By continuing, you agree to our{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Terms of Service</a>{' '}and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Privacy Policy</a>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
